import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { STATUS_LABELS, SOURCE_LABELS, formatDateISO, formatTimePretty } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { SignOut, UserPlus, CalendarBlank, Clock, CheckCircle, Circle, Trash, ArrowClockwise, XCircle } from "@phosphor-icons/react";

const STATUS_COLORS = {
    booked: "bg-stone text-ink border-divider",
    arrived: "bg-moss text-bone border-moss",
    in_progress: "bg-terracotta text-white border-terracotta",
    completed: "bg-bone text-ash border-divider line-through",
    no_show: "bg-bone text-ash border-divider italic",
    cancelled: "bg-bone text-ash border-divider line-through",
};

export default function StaffDashboard() {
    const nav = useNavigate();
    const [staff, setStaff] = useState(null);
    const [date, setDate] = useState(new Date());
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddStaff, setShowAddStaff] = useState(false);
    const [newStaff, setNewStaff] = useState({ name: "", pin: "" });

    useEffect(() => {
        if (!localStorage.getItem("staff_token")) {
            nav("/staff/login");
            return;
        }
        api.get("/staff/me").then((r) => setStaff(r.data)).catch(() => {
            localStorage.removeItem("staff_token");
            nav("/staff/login");
        });
    }, [nav]);

    const load = async () => {
        setLoading(true);
        try {
            const iso = formatDateISO(date);
            const { data } = await api.get(`/appointments?date=${iso}`);
            setList(data);
        } catch {
            toast.error("Failed to load appointments");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (staff) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staff, date]);

    const stats = useMemo(() => {
        const s = { total: list.length, booked: 0, arrived: 0, in_progress: 0, completed: 0 };
        list.forEach((a) => { if (s[a.status] !== undefined) s[a.status]++; });
        return s;
    }, [list]);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/appointments/${id}/status`, { status });
            toast.success(`Marked ${STATUS_LABELS[status]}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this appointment?")) return;
        try {
            await api.delete(`/appointments/${id}`);
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const cancelAppt = async (id) => {
        if (!window.confirm("Cancel this appointment? The slot will open back up.")) return;
        try {
            await api.patch(`/appointments/${id}/status`, { status: "cancelled" });
            toast.success("Appointment cancelled");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Cancel failed");
        }
    };

    const addStaff = async () => {
        if (!newStaff.name.trim() || !/^[0-9]{3,8}$/.test(newStaff.pin)) {
            toast.error("Name required; PIN 3–8 digits");
            return;
        }
        try {
            await api.post("/staff", newStaff);
            toast.success("Staff created");
            setShowAddStaff(false);
            setNewStaff({ name: "", pin: "" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        }
    };

    const logout = () => {
        localStorage.removeItem("staff_token");
        localStorage.removeItem("staff_name");
        nav("/staff/login");
    };

    return (
        <div className="min-h-screen bg-bone text-ink">
            <header className="border-b border-divider bg-bone/95 backdrop-blur sticky top-0 z-30">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Logo size={32} className="text-moss" />
                        <div>
                            <div className="font-serif text-lg leading-none">Noble Accounting Services</div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-ash mt-1">
                                {staff ? `Dashboard · ${staff.name}` : "Dashboard"}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowAddStaff(true)}
                            className="hidden md:inline-flex items-center gap-2 border border-divider hover:border-moss px-4 py-2 text-xs uppercase tracking-[0.2em]"
                            data-testid="add-staff-btn"
                        >
                            <UserPlus size={14} /> Add staff
                        </button>
                        <button
                            onClick={load}
                            className="inline-flex items-center gap-2 border border-divider hover:border-moss px-4 py-2 text-xs uppercase tracking-[0.2em]"
                            data-testid="refresh-btn"
                        >
                            <ArrowClockwise size={14} /> Refresh
                        </button>
                        <button
                            onClick={logout}
                            className="inline-flex items-center gap-2 bg-ink text-bone px-4 py-2 text-xs uppercase tracking-[0.2em]"
                            data-testid="logout-btn"
                        >
                            <SignOut size={14} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
                <aside className="lg:col-span-3 space-y-6">
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-ash mb-3 flex items-center gap-2">
                            <CalendarBlank size={14} /> Select day
                        </div>
                        <div className="border border-divider bg-bone p-2 inline-block w-full">
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={(d) => d && setDate(d)}
                                classNames={{
                                    day_selected: "bg-moss text-bone hover:bg-moss-hover",
                                    day_today: "bg-stone text-ink font-semibold",
                                }}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-0 border-t border-l border-divider" data-testid="stats-grid">
                        {[
                            { label: "Total", value: stats.total },
                            { label: "Booked", value: stats.booked },
                            { label: "Arrived", value: stats.arrived },
                            { label: "Completed", value: stats.completed },
                        ].map((s) => (
                            <div key={s.label} className="border-r border-b border-divider p-4">
                                <div className="text-xs uppercase tracking-[0.2em] text-ash">{s.label}</div>
                                <div className="font-serif text-3xl mt-1">{s.value}</div>
                            </div>
                        ))}
                    </div>
                </aside>

                <main className="lg:col-span-9">
                    <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                        <div>
                            <p className="text-xs uppercase tracking-[0.25em] text-ash">Appointments</p>
                            <h1 className="font-serif text-4xl font-light">{date.toDateString()}</h1>
                        </div>
                        <div className="text-sm text-ash">
                            {loading ? "Loading…" : `${list.length} record${list.length === 1 ? "" : "s"}`}
                        </div>
                    </div>

                    {list.length === 0 && !loading ? (
                        <div className="border border-dashed border-divider p-16 text-center text-ash">
                            No appointments for this date.
                        </div>
                    ) : (
                        <div className="border-t border-l border-divider" data-testid="appointments-table">
                            <div className="hidden md:grid grid-cols-12 border-r border-b border-divider bg-stone/60 text-[10px] uppercase tracking-[0.2em] text-ash">
                                <div className="col-span-2 p-3 border-r border-divider">Time</div>
                                <div className="col-span-3 p-3 border-r border-divider">Client</div>
                                <div className="col-span-2 p-3 border-r border-divider">Service</div>
                                <div className="col-span-1 p-3 border-r border-divider">Source</div>
                                <div className="col-span-2 p-3 border-r border-divider">Status</div>
                                <div className="col-span-2 p-3">Actions</div>
                            </div>
                            {list.map((a) => (
                                <div key={a.id} className="grid grid-cols-1 md:grid-cols-12 border-r border-b border-divider bg-bone hover:bg-stone/30 transition-colors" data-testid={`appt-row-${a.id}`}>
                                    <div className="md:col-span-2 p-3 md:border-r border-divider font-mono text-sm flex items-center gap-2">
                                        <Clock size={14} className="text-ash" /> {formatTimePretty(a.appointment_time)}
                                    </div>
                                    <div className="md:col-span-3 p-3 md:border-r border-divider">
                                        <div className="text-sm text-ink">{a.customer_name}</div>
                                        <div className="text-xs text-ash font-mono">{a.customer_phone}</div>
                                    </div>
                                    <div className="md:col-span-2 p-3 md:border-r border-divider text-sm">{a.service_name}</div>
                                    <div className="md:col-span-1 p-3 md:border-r border-divider text-xs uppercase tracking-[0.15em] text-ash">{SOURCE_LABELS[a.source]}</div>
                                    <div className="md:col-span-2 p-3 md:border-r border-divider">
                                        <span className={`inline-block border px-2 py-1 text-[10px] uppercase tracking-[0.15em] ${STATUS_COLORS[a.status]}`} data-testid={`appt-status-${a.id}`}>
                                            {STATUS_LABELS[a.status]}
                                        </span>
                                    </div>
                                    <div className="md:col-span-2 p-3 flex flex-wrap gap-1.5">
                                        {a.status === "booked" && (
                                            <IconBtn title="Mark arrived" onClick={() => updateStatus(a.id, "arrived")} testid={`mark-arrived-${a.id}`}>
                                                <CheckCircle size={14} />
                                            </IconBtn>
                                        )}
                                        {a.status === "arrived" && (
                                            <IconBtn title="Start session" onClick={() => updateStatus(a.id, "in_progress")} testid={`mark-in-progress-${a.id}`}>
                                                <Clock size={14} />
                                            </IconBtn>
                                        )}
                                        {a.status === "in_progress" && (
                                            <IconBtn title="Mark complete" onClick={() => updateStatus(a.id, "completed")} testid={`mark-completed-${a.id}`}>
                                                <CheckCircle size={14} />
                                            </IconBtn>
                                        )}
                                        {a.status !== "completed" && a.status !== "cancelled" && a.status !== "no_show" && (
                                            <IconBtn title="No-show" onClick={() => updateStatus(a.id, "no_show")} testid={`mark-noshow-${a.id}`}>
                                                <Circle size={14} />
                                            </IconBtn>
                                        )}
                                        {a.status !== "cancelled" && a.status !== "completed" && (
                                            <IconBtn title="Cancel appointment" onClick={() => cancelAppt(a.id)} variant="warning" testid={`cancel-${a.id}`}>
                                                <XCircle size={14} />
                                            </IconBtn>
                                        )}
                                        <IconBtn title="Delete" onClick={() => remove(a.id)} variant="danger" testid={`delete-${a.id}`}>
                                            <Trash size={14} />
                                        </IconBtn>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>

            {showAddStaff && (
                <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-6" onClick={() => setShowAddStaff(false)}>
                    <div className="bg-bone border border-divider w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="add-staff-modal">
                        <h3 className="font-serif text-3xl mb-6">Add staff member</h3>
                        <div className="space-y-4">
                            <label className="block">
                                <div className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Name</div>
                                <input
                                    value={newStaff.name}
                                    onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                                    className="w-full bg-bone border border-divider rounded-none px-4 py-3 focus:outline-none focus:border-moss"
                                    data-testid="new-staff-name"
                                />
                            </label>
                            <label className="block">
                                <div className="text-xs uppercase tracking-[0.2em] text-ash mb-2">PIN (3–8 digits)</div>
                                <input
                                    value={newStaff.pin}
                                    onChange={(e) => setNewStaff({ ...newStaff, pin: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                                    className="w-full bg-bone border border-divider rounded-none px-4 py-3 focus:outline-none focus:border-moss font-mono"
                                    data-testid="new-staff-pin"
                                />
                            </label>
                            <div className="flex gap-3 pt-2">
                                <button onClick={() => setShowAddStaff(false)} className="flex-1 border border-ink py-3 text-xs uppercase tracking-[0.2em]" data-testid="new-staff-cancel">
                                    Cancel
                                </button>
                                <button onClick={addStaff} className="flex-1 bg-moss text-bone py-3 text-xs uppercase tracking-[0.2em]" data-testid="new-staff-save">
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function IconBtn({ children, onClick, title, variant, testid }) {
    const base = "inline-flex items-center justify-center w-8 h-8 border transition-colors";
    const styles = {
        danger: "border-divider text-ash hover:text-bone hover:bg-destructive hover:border-destructive",
        warning: "border-divider text-ash hover:text-bone hover:bg-terracotta hover:border-terracotta",
        default: "border-divider text-ash hover:text-bone hover:bg-moss hover:border-moss",
    };
    return (
        <button
            onClick={onClick}
            title={title}
            data-testid={testid}
            className={`${base} ${styles[variant] || styles.default}`}
        >
            {children}
        </button>
    );
}
